import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";

// Public partner page (/locker). A partner site enters its site code, sees
// the 18-locker board, taps a locker to verify the client (photo, name, DOB)
// and records dated dose entries (default 7 tablets, backdatable, locked on
// submit, cancellable until HQ marks them paid). No money shown to partners.

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#1F3A5F";
const GREEN = "#2E7D5B";
const CREAM = "#F5F3EF";
const BORDER = "#E3E0D9";
const MUTED = "#6B7280";
const TEXT = "#1F2937";
const RED = "#B4351B";

const S = {
  page: { minHeight: "100vh", background: CREAM, fontFamily: "'Inter', system-ui, sans-serif", color: "#1F2937" },
  wrap: { maxWidth: 560, margin: "0 auto", padding: "0 16px 48px", paddingTop: 88 },
  card: { background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,.05)" },
  input: { width: "100%", height: 46, padding: "0 14px", fontSize: 16, letterSpacing: 2, textTransform: "uppercase", border: `1px solid ${BORDER}`, borderRadius: 10, boxSizing: "border-box", textAlign: "center", fontWeight: 700 },
  btnPrimary: { width: "100%", height: 46, background: NAVY, color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer" },
  btnGreen: { width: "100%", height: 50, background: GREEN, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer" },
  err: { background: "#FBEAE5", border: "1px solid #E9BFB1", color: RED, borderRadius: 10, padding: "10px 12px", fontSize: 13, fontWeight: 600, marginTop: 10 },
};

// Inline-edit dropdowns (Storage / Consultation in the patient profile).
// The arrow itself comes from the global select rule in index.css — one clean
// chevron everywhere, no OS-native arrow.
const editSelect = { flex: 1, minWidth: 0, height: 28, padding: "0 26px 0 8px", fontSize: 12, fontWeight: 600, color: TEXT, background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 7 };

const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "");
const fmtCash = (n) => `$${Number(n || 0).toFixed(2)}`;
const cash0 = (n) => `$${Math.round(Number(n) || 0).toLocaleString("en-CA")}`;

// The physical locker grid + board filters are hidden for now — the Patients
// list opens the exact same client popup, so the grid adds no value. All the
// functionality is intact; flip to true to bring it back.
const SHOW_LOCKER_GRID = false;

// Formats a typed phone number live as (555) 555-9999 (digits only, 10 max).
const fmtPhone = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 10);
  if (d.length > 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length > 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return d ? `(${d}` : "";
};

// Auto-growing textarea — always shows at least `minRows` lines and expands
// with its content, so full notes are readable without in-box scrolling.
function AutoArea({ value, style, minRows = 4, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(minRows * 20 + 16, el.scrollHeight)}px`;
  }, [value, minRows]);
  return <textarea ref={ref} rows={minRows} value={value} style={{ ...style, overflow: "hidden", resize: "none", lineHeight: "20px" }} {...props} />;
}

// Entry history lists (medication + cash) show at most 4 rows so the popup
// never grows; small top chevrons page left/right through older entries.
// Invisible padding rows keep the block — and the popup — a constant height.
const ENTRY_PAGE = 4;

function PagedEntries({ entries, page, setPage, kind, onRemove }) {
  const total = entries.length;
  const pages = Math.max(1, Math.ceil(total / ENTRY_PAGE));
  const p = Math.min(page, pages - 1);
  const slice = entries.slice(p * ENTRY_PAGE, p * ENTRY_PAGE + ENTRY_PAGE);
  const slots = [...slice];
  while (slots.length < ENTRY_PAGE) slots.push(null);
  const pillBase = { fontSize: 10, fontWeight: 800, borderRadius: 99, padding: "2px 8px", flexShrink: 0, whiteSpace: "nowrap", letterSpacing: ".06em" };
  const rowStyle = { display: "flex", alignItems: "center", gap: 6, padding: "5px 0", borderTop: `1px solid ${BORDER}`, fontSize: 12 };
  const arrowStyle = (disabled) => ({ background: "none", border: "none", padding: 2, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.25 : 1, color: NAVY, display: "inline-flex", alignItems: "center", fontFamily: "inherit" });
  const idPrefix = kind === "cash" ? "locker-cash" : "locker";
  return (
    <div data-testid={kind === "cash" ? "locker-cash-entries" : "locker-modal-weeks"} style={{ marginTop: 8, textAlign: "left", position: "relative" }}>
      {/* pager row — always visible (chevrons dim at the ends) so it never
          pops in later and the frame never changes size */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, marginBottom: 2, minHeight: 16 }}>
        <button type="button" data-testid={`${idPrefix}-entries-prev`} disabled={p === 0} onClick={() => setPage(p - 1)} style={arrowStyle(p === 0)} aria-label="Newer entries">
          <ChevronLeft style={{ width: 14, height: 14 }} strokeWidth={2.5} />
        </button>
        <span data-testid={`${idPrefix}-entries-range`} style={{ fontSize: 10, fontWeight: 700, color: MUTED }}>
          {total === 0 ? "0 of 0" : `${p * ENTRY_PAGE + 1}–${Math.min(total, (p + 1) * ENTRY_PAGE)} of ${total}`}
        </span>
        <button type="button" data-testid={`${idPrefix}-entries-next`} disabled={p >= pages - 1} onClick={() => setPage(p + 1)} style={arrowStyle(p >= pages - 1)} aria-label="Older entries">
          <ChevronRight style={{ width: 14, height: 14 }} strokeWidth={2.5} />
        </button>
      </div>
      {slots.map((w, i) => (w ? (
        <div key={w.id} data-testid={kind === "cash" ? `locker-cash-entry-${w.id}` : `locker-modal-entry-${w.id}`} style={rowStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <b>{fmtDay(w.service_date)}</b>
          </div>
          {kind === "cash" ? (
            <span data-testid={`locker-cash-amount-${w.id}`} style={{ ...pillBase, color: GREEN, border: `1.5px solid ${GREEN}` }}>{fmtCash(w.amount)}</span>
          ) : (
            <span style={{ ...pillBase, textTransform: "uppercase", color: NAVY, border: `1.5px solid ${NAVY}` }}>{w.tablets} tabs</span>
          )}
          {w.status === "submitted" ? (
            <button type="button"
              data-testid={kind === "cash" ? `locker-cash-remove-${w.id}` : `locker-entry-remove-${w.id}`}
              onClick={() => onRemove(kind, w)}
              style={{ ...pillBase, textTransform: "uppercase", color: RED, border: `1.5px solid ${RED}`, background: "#fff", cursor: "pointer", fontFamily: "inherit" }}>
              Remove
            </button>
          ) : (
            <span data-testid={kind === "cash" ? `locker-cash-locked-${w.id}` : `locker-entry-locked-${w.id}`}
              style={{ ...pillBase, textTransform: "uppercase", color: MUTED, border: `1.5px solid ${MUTED}` }}>Locked</span>
          )}
        </div>
      ) : (
        <div key={`pad-${i}`} aria-hidden="true" style={{ ...rowStyle, borderTop: "1px solid transparent", visibility: "hidden" }}>
          <div style={{ flex: 1, minWidth: 0 }}><b> </b></div>
          <span style={{ ...pillBase, border: "1.5px solid transparent" }}> </span>
        </div>
      )))}
      {total === 0 && (
        <div data-testid={`${idPrefix}-entries-empty`}
          style={{ position: "absolute", top: 18, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: MUTED, pointerEvents: "none" }}>
          No entries yet.
        </div>
      )}
    </div>
  );
}

export default function Locker() {
  const [token, setToken] = useState(() => sessionStorage.getItem("locker.token") || "");
  const [kind, setKind] = useState(() => sessionStorage.getItem("locker.kind") || "site"); // "site" | "staff"
  const [siteName, setSiteName] = useState(() => sessionStorage.getItem("locker.site") || "");
  const [siteAddr, setSiteAddr] = useState({ street: "", region: "" });
  const [cashBal, setCashBal] = useState(null);
  const [code, setCode] = useState("");
  const [entering, setEntering] = useState(false);
  const [enterErr, setEnterErr] = useState("");

  // Staff portal (adherence payments)
  const [staffHome, setStaffHome] = useState(null);
  const [paysOpen, setPaysOpen] = useState(false);
  const [feesOpen, setFeesOpen] = useState(false);
  const [confirming, setConfirming] = useState("");

  const [board, setBoard] = useState(null);
  const [boardErr, setBoardErr] = useState("");
  const [selected, setSelected] = useState(null); // board cell
  const [dispensing, setDispensing] = useState(false);
  const [dispenseErr, setDispenseErr] = useState("");
  const [dispensedMsg, setDispensedMsg] = useState("");
  const [photoFull, setPhotoFull] = useState(false);
  const [tabsAmt, setTabsAmt] = useState("7");
  const [svcDate, setSvcDate] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [fStatus, setFStatus] = useState("");
  const [fLoc, setFLoc] = useState("");
  const [fConsult, setFConsult] = useState("");
  const [cashOpen, setCashOpen] = useState(false);
  const [cashAmt, setCashAmt] = useState("");
  const [cashDate, setCashDate] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [cashBusy, setCashBusy] = useState(false);
  const [cashErr, setCashErr] = useState("");
  const [cashMsg, setCashMsg] = useState("");
  const [medPage, setMedPage] = useState(0);
  const [cashPage, setCashPage] = useState(0);
  const [locations, setLocations] = useState([]);
  const [locEdit, setLocEdit] = useState(false);
  const [locChoice, setLocChoice] = useState("");
  const [locBusy, setLocBusy] = useState(false);
  const [locErr, setLocErr] = useState("");
  const [conEdit, setConEdit] = useState(false);
  const [conChoice, setConChoice] = useState("");
  const [conBusy, setConBusy] = useState(false);
  const [conErr, setConErr] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesPhone, setNotesPhone] = useState("");
  const [notesAddr, setNotesAddr] = useState("");
  const [notesInfo, setNotesInfo] = useState("");
  const [notesBusy, setNotesBusy] = useState(false);
  const [notesErr, setNotesErr] = useState("");
  const [patOpen, setPatOpen] = useState(false); // Patients list starts minimized
  const [refreshing, setRefreshing] = useState(false);

  const loadBoard = useCallback(async (tok) => {
    try {
      const resp = await axios.get(`${BACKEND}/api/locker/portal/board`, {
        headers: { "X-Auth-Token": tok },
      });
      setBoard(resp.data.board);
      // Preload client ID photos into the browser cache right away so
      // opening a patient profile shows the photo instantly, not after a fetch.
      for (const cell of resp.data.board || []) {
        const c = cell?.client;
        if (c?.has_photo && c?.photo_url) {
          const img = new Image();
          img.src = `${BACKEND}${c.photo_url}?token=${tok}`;
        }
      }
      setCashBal(resp.data.cash_balance || null);
      setLocations(resp.data.locations || []);
      setSiteName(resp.data.site_name);
      setSiteAddr({
        street: resp.data.site_street || "",
        region: resp.data.site_region || "",
      });
      setBoardErr("");
      return resp.data.board;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not load the locker board";
      setBoardErr(msg);
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        sessionStorage.removeItem("locker.token");
        sessionStorage.removeItem("locker.site");
        sessionStorage.removeItem("locker.kind");
        setToken("");
      }
      return null;
    }
  }, []);

  const loadStaffHome = useCallback(async (tok) => {
    try {
      const resp = await axios.get(`${BACKEND}/api/locker/staff/home`, {
        headers: { "X-Auth-Token": tok },
      });
      setStaffHome(resp.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        sessionStorage.removeItem("locker.token");
        sessionStorage.removeItem("locker.kind");
        setToken("");
        setKind("site");
      }
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    if (kind === "staff") { loadStaffHome(token); loadBoard(token); } else loadBoard(token);
  }, [token, kind, loadBoard, loadStaffHome]);

  // Always open the board at the very top so the site header is fully visible.
  useEffect(() => { window.scrollTo(0, 0); }, [token]);

  // Hard-lock the page while a popup is open (iOS Safari rubber-bands the
  // body behind fixed overlays). Body goes position:fixed at the current
  // scroll offset; on close we unpin and restore the exact position.
  const popupWasOpen = useRef(false);
  const popupScroll = useRef(0);
  useEffect(() => {
    const open = Boolean(selected) || photoFull;
    if (open && !popupWasOpen.current) {
      popupScroll.current = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${popupScroll.current}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.overflow = "hidden";
    }
    if (!open && popupWasOpen.current) {
      const y = popupScroll.current;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      window.scrollTo(0, y);
    }
    popupWasOpen.current = open;
  }, [selected, photoFull]);

  const logout = () => {
    sessionStorage.removeItem("locker.token");
    sessionStorage.removeItem("locker.kind");
    sessionStorage.removeItem("locker.site");
    setToken("");
    setKind("site");
    setBoard(null);
    setStaffHome(null);
    setCode("");
    setEnterErr("");
  };

  const enter = async () => {
    if (!code.trim()) return;
    setEntering(true); setEnterErr("");
    try {
      const resp = await axios.post(`${BACKEND}/api/locker/auth/enter`, { code: code.trim() });
      const k = resp.data.kind === "staff" ? "staff" : "site";
      sessionStorage.setItem("locker.token", resp.data.token);
      sessionStorage.setItem("locker.kind", k);
      setKind(k);
      if (k === "staff") {
        sessionStorage.removeItem("locker.site");
        setStaffHome(null);
      } else {
        sessionStorage.setItem("locker.site", resp.data.site_name);
        setSiteName(resp.data.site_name);
      }
      setToken(resp.data.token);
    } catch (e) {
      setEnterErr(e?.response?.data?.detail || "Could not sign in — try again");
    } finally {
      setEntering(false);
    }
  };

  const confirmPayment = async (pid) => {
    setConfirming(pid);
    try {
      await axios.post(`${BACKEND}/api/locker/staff/payments/${pid}/confirm`, {}, { headers: { "X-Auth-Token": token } });
      await loadStaffHome(token);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not confirm — try again");
    } finally {
      setConfirming("");
    }
  };

  const dispense = async () => {
    if (!selected) return;
    const tabs = Number(tabsAmt) || 0;
    if (tabs < 1) { setDispenseErr("Enter the number of tablets given"); return; }
    if (!svcDate) { setDispenseErr("Pick the date the medication was given"); return; }
    if (!window.confirm(`Record ${tabs} tablets for ${selected.client.first_name} ${selected.client.last_name} on ${svcDate}?\n\nYou can remove this entry from this screen until HepCURE processes it — after that it is LOCKED.`)) return;
    setDispensing(true); setDispenseErr(""); setDispensedMsg("");
    try {
      const resp = await axios.post(`${BACKEND}/api/locker/portal/dispense`,
        { client_id: selected.client.id, compartment: selected.n || undefined, tablets: tabs, service_date: svcDate }, { headers: { "X-Auth-Token": token } });
      setDispensedMsg(resp.data.message);
      const fresh = await loadBoard(token);
      const cell = fresh?.find((x) => x.client?.id === selected.client.id);
      if (cell) setSelected(cell);
      setTabsAmt("7"); setSvcDate(new Date().toLocaleDateString("en-CA"));
    } catch (e) {
      setDispenseErr(e?.response?.data?.detail || "Could not submit — try again");
    } finally {
      setDispensing(false);
    }
  };

  // Partner-side removal of their own entry — only while HepCURE hasn't
  // processed it (backend enforces: submitted only, this site only).
  const removeEntry = async (kind, w) => {
    if (!selected) return;
    const what = kind === "cash" ? `${fmtMoney(w.amount)} cash` : `${w.tablets} tablets`;
    if (!window.confirm(`Remove the ${fmtD(w.service_date)} entry (${what}) for ${selected.client.first_name} ${selected.client.last_name}?`)) return;
    try {
      await axios.delete(`${BACKEND}/api/locker/portal/${kind === "cash" ? "cash" : "pickups"}/${w.id}`, { headers: { "X-Auth-Token": token } });
      const fresh = await loadBoard(token);
      const cell = fresh?.find((x) => x.client?.id === selected.client.id);
      if (cell) setSelected(cell);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not remove — try again";
      if (kind === "cash") setCashErr(msg); else setDispenseErr(msg);
    }
  };

  const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "");
  const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
  // Summary tiles: round numbers with thousands separators ($7,000 not $7,000.00)
  const fmtMoney0 = (n) => `$${Math.round(Number(n || 0)).toLocaleString("en-CA")}`;

  // Partner-side storage change — the site moves the medication themselves.
  // Same open routine whether the partner taps a locker cell or a Patients row.
  const openCell = (cell) => {
    setSelected(cell); setDispenseErr(""); setDispensedMsg(""); setTabsAmt("7");
    setSvcDate(new Date().toLocaleDateString("en-CA")); setCashOpen(false);
    setMedPage(0); setCashPage(0); setLocEdit(false); setLocErr("");
    setConEdit(false); setConErr("");
    setNotesOpen(false); setNotesErr("");
  };

  const saveLocation = async () => {
    if (!selected) return;
    setLocBusy(true); setLocErr("");
    try {
      await axios.patch(`${BACKEND}/api/locker/portal/clients/${selected.client.id}/location`,
        { location: locChoice }, { headers: { "X-Auth-Token": token } });
      const fresh = await loadBoard(token);
      const cell = fresh?.find((x) => x.client?.id === selected.client.id);
      if (cell) setSelected(cell);
      setLocEdit(false);
    } catch (e) {
      setLocErr(e?.response?.data?.detail || "Could not save — try again");
    } finally {
      setLocBusy(false);
    }
  };

  // Partner confirms the consultation is completed (required before cash).
  const saveConsultation = async () => {
    if (!selected) return;
    setConBusy(true); setConErr("");
    try {
      await axios.patch(`${BACKEND}/api/locker/portal/clients/${selected.client.id}/consultation`,
        { consultation: conChoice }, { headers: { "X-Auth-Token": token } });
      const fresh = await loadBoard(token);
      const cell = fresh?.find((x) => x.client?.id === selected.client.id);
      if (cell) setSelected(cell);
      setConEdit(false);
    } catch (e) {
      setConErr(e?.response?.data?.detail || "Could not save — try again");
    } finally {
      setConBusy(false);
    }
  };

  // Partner-side notes — either end (site or HepCURE admin) can update them.
  // Three fields saved together: phone, Address / Contact, Additional info.
  const selClient = selected?.client;
  const hasNotes = Boolean(selClient && ((selClient.phone || "").trim() || (selClient.notes || "").trim() || (selClient.notes_additional || "").trim()));
  const notesDirty = selected
    ? notesPhone !== (selected.client.phone || "")
      || notesAddr !== (selected.client.notes || "")
      || notesInfo !== (selected.client.notes_additional || "")
    : false;
  const saveNotes = async () => {
    if (!selected) return;
    setNotesBusy(true); setNotesErr("");
    try {
      await axios.patch(`${BACKEND}/api/locker/portal/clients/${selected.client.id}/notes`,
        { phone: notesPhone, address_contact: notesAddr, notes_additional: notesInfo },
        { headers: { "X-Auth-Token": token } });
      const fresh = await loadBoard(token);
      const cell = fresh?.find((x) => x.client?.id === selected.client.id);
      if (cell) setSelected(cell);
    } catch (e) {
      setNotesErr(e?.response?.data?.detail || "Could not save — try again");
    } finally {
      setNotesBusy(false);
    }
  };

  const recordCash = async () => {
    if (!selected) return;
    const amt = Math.round((Number(cashAmt) || 0) * 100) / 100;
    if (!(amt > 0)) { setCashErr("Enter the cash amount given"); return; }
    if (!cashDate) { setCashErr("Pick the date the cash was given"); return; }
    if (!window.confirm(`Record ${fmtMoney(amt)} cash for ${selected.client.first_name} ${selected.client.last_name} on ${cashDate}?\n\nYou can remove this entry from this screen until HepCURE processes it — after that it is LOCKED.`)) return;
    setCashBusy(true); setCashErr(""); setCashMsg("");
    try {
      const resp = await axios.post(`${BACKEND}/api/locker/portal/cash`,
        { client_id: selected.client.id, compartment: selected.n || undefined, amount: amt, service_date: cashDate }, { headers: { "X-Auth-Token": token } });
      setCashMsg(resp.data.message);
      const fresh = await loadBoard(token);
      const cell = fresh?.find((x) => x.client?.id === selected.client.id);
      if (cell) setSelected(cell);
      setCashAmt(""); setCashDate(new Date().toLocaleDateString("en-CA"));
    } catch (e) {
      setCashErr(e?.response?.data?.detail || "Could not submit — try again");
    } finally {
      setCashBusy(false);
    }
  };

  const photoOf = (c) => `${BACKEND}${c.photo_url}?token=${token}`;

  // Partner "Patients" list — every client at this site, alphabetical (same as admin).
  const patientRows = useMemo(() => (board || [])
    .filter((cell) => cell.occupied && cell.client)
    .map((cell) => ({ cell, c: cell.client }))
    .sort((a, b) => `${a.c.first_name} ${a.c.last_name}`.localeCompare(`${b.c.first_name} ${b.c.last_name}`)),
  [board]);

  // ---------- board filters (disposition + storage, combinable) ----------
  const statusOpts = useMemo(() => {
    const s = new Set();
    (board || []).forEach((cell) => { if (cell.occupied && cell.client) s.add(cell.client.client_status || "Active"); });
    return [...s].sort();
  }, [board]);
  const locOpts = useMemo(() => {
    const s = new Set();
    (board || []).forEach((cell) => { const l = (cell.client?.location || "").trim(); if (cell.occupied && l) s.add(l); });
    return [...s].sort();
  }, [board]);
  const filtering = Boolean(fStatus || fLoc || fConsult);
  const cellMatch = (cell) => {
    const c = cell.client;
    if (!cell.occupied || !c) return false;
    if (fStatus && (c.client_status || "Active") !== fStatus) return false;
    if (fLoc && (c.location || "") !== fLoc) return false;
    if (fConsult && (c.consultation || "no") !== fConsult) return false;
    return true;
  };
  const occupiedCount = (board || []).filter((cell) => cell.occupied && cell.client).length;
  const matchCount = filtering ? (board || []).filter(cellMatch).length : 0;

  // ---------- code entry ----------
  if (!token) {
    return (
      <div style={S.page} data-testid="locker-entry-page">
        <div style={{ ...S.wrap, maxWidth: 400, paddingTop: 96 }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: NAVY, letterSpacing: -0.5 }}>HepCURE</div>
            <div style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>Medication Adherence — Partner Site</div>
          </div>
          <div style={S.card}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, textAlign: "center" }}>Enter your site code</div>
            <input
              data-testid="locker-code-input"
              style={S.input}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && enter()}
              autoCapitalize="characters"
            />
            {enterErr && <div style={S.err} data-testid="locker-entry-error">{enterErr}</div>}
            <button
              data-testid="locker-enter-button"
              style={{ ...S.btnPrimary, marginTop: 14, opacity: entering ? 0.7 : 1 }}
              onClick={enter}
              disabled={entering}
            >
              {entering ? "Checking…" : "Open adherence board"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- staff extras: cash position tiles, client progress, payments ----------
  const staffCash = staffHome?.cash || { qualified: 0, topups_total: 0, given: 0, remaining: 0 };
  const staffPays = staffHome?.payments || [];
  const staffPaysTotal = staffPays.reduce((a, p) => a + (p.amount || 0), 0);
  const staffTilesCard = kind === "staff" ? (
    <>
          {/* cash position across their assigned clients — same four figures
              as the site-level card: authorized, topped up, given, on hand */}
          <div style={{ ...S.card, marginBottom: 14, padding: "10px 10px", display: "flex", gap: 6, flexWrap: "nowrap" }} data-testid="locker-staff-tiles">
            <div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>Qualified</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: NAVY, marginTop: 2, whiteSpace: "nowrap" }} data-testid="locker-staff-tile-qualified">{cash0(staffCash.qualified)}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>Cash in</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: NAVY, marginTop: 2, whiteSpace: "nowrap" }} data-testid="locker-staff-tile-cash-in">{cash0(staffCash.topups_total)}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>Given</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: NAVY, marginTop: 2, whiteSpace: "nowrap" }} data-testid="locker-staff-tile-given">{cash0(staffCash.given)}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>On hand</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: staffCash.remaining < 0 ? RED : GREEN, marginTop: 2, whiteSpace: "nowrap" }} data-testid="locker-staff-tile-on-hand">{cash0(staffCash.remaining)}</div>
            </div>
          </div>
    </>
  ) : null;

  // fees + payments render below the Patients list (patients are the daily job)
  const staffPayCards = kind === "staff" ? (
    <>

          {/* your fees — per-client progress toward the $600 cap; same
              collapsible format as Payments below */}
          <div style={{ ...S.card, padding: "12px 18px", marginBottom: 14 }} data-testid="locker-staff-fees">
            <button type="button" data-testid="locker-staff-fees-toggle" onClick={() => setFeesOpen(!feesOpen)}
              style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: MUTED, textAlign: "left" }}>
                Your fees {(staffHome?.clients || []).length > 0 && `(${(staffHome?.clients || []).length})`}
              </span>
              {feesOpen ? <ChevronDown style={{ width: 14, height: 14, color: MUTED, flexShrink: 0 }} /> : <ChevronRight style={{ width: 14, height: 14, color: MUTED, flexShrink: 0 }} />}
              <span style={{ flex: 1 }} />
              {(staffHome?.clients || []).length > 0 && (
                <span style={{ fontSize: 12, fontWeight: 800, color: GREEN, textAlign: "right" }}>
                  {cash0((staffHome?.clients || []).reduce((a, c) => a + (c.paid || 0), 0))} of {cash0((staffHome?.clients || []).reduce((a, c) => a + (c.cap || 0), 0))}
                </span>
              )}
            </button>
            {feesOpen && (
              <div style={{ marginTop: 4 }} data-testid="locker-staff-fees-list">
                {(staffHome?.clients || []).length === 0 && (
                  <div style={{ fontSize: 12.5, color: MUTED, padding: "8px 0", textAlign: "center" }} data-testid="locker-staff-clients-empty">No clients assigned to you yet.</div>
                )}
                {(staffHome?.clients || []).map((c) => (
                  <div key={c.id} data-testid={`locker-staff-client-${c.id}`} style={{ padding: "9px 0", borderTop: `1px solid ${BORDER}` }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700 }}>{c.name}</span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: NAVY, whiteSpace: "nowrap", textAlign: "right" }}>{cash0(c.paid)} of {cash0(c.cap)}</span>
                    </div>
                    <div style={{ fontSize: 11, color: MUTED, marginTop: 1 }}>Week {c.week_no} of {c.max_weeks} · {cash0(c.fee)} per week</div>
                    <div style={{ height: 4, background: "#EFEDE7", borderRadius: 99, marginTop: 6, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${Math.min(100, (c.paid / (c.cap || 1)) * 100)}%`, background: GREEN, borderRadius: 99 }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* payments — one line per dated bulk payment; confirm on receipt */}
          <div style={{ ...S.card, padding: "12px 18px", marginBottom: 14 }} data-testid="locker-staff-payments">
            <button type="button" data-testid="locker-staff-payments-toggle" onClick={() => setPaysOpen(!paysOpen)}
              style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: MUTED, textAlign: "left" }}>
                Payments {staffPays.length > 0 && `(${staffPays.length})`}
              </span>
              {paysOpen ? <ChevronDown style={{ width: 14, height: 14, color: MUTED, flexShrink: 0 }} /> : <ChevronRight style={{ width: 14, height: 14, color: MUTED, flexShrink: 0 }} />}
              <span style={{ flex: 1 }} />
              {staffPays.length > 0 && <span style={{ fontSize: 12, fontWeight: 800, color: GREEN, textAlign: "right" }}>{cash0(staffPaysTotal)}</span>}
            </button>
            {paysOpen && (
              <div style={{ marginTop: 4 }} data-testid="locker-staff-payments-list">
                {staffPays.length === 0 ? (
                  <div style={{ fontSize: 12, color: MUTED, padding: "8px 0", textAlign: "center" }} data-testid="locker-staff-payments-empty">No payments issued yet.</div>
                ) : staffPays.map((p) => (
                  <div key={p.id} data-testid={`locker-staffpay-${p.id}`}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 0", borderTop: `1px solid ${BORDER}`, fontSize: 12.5 }}>
                    <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600, fontFamily: "inherit" }}>{fmtDay(p.date)}</span>
                    {p.status === "confirmed" ? (
                      <span data-testid={`locker-staffpay-status-${p.id}`} style={{ fontSize: 11, fontWeight: 800, color: GREEN, background: "#E7F2EC", borderRadius: 99, padding: "5px 0", minWidth: 92, textAlign: "center", whiteSpace: "nowrap", boxSizing: "border-box" }}>Confirmed</span>
                    ) : (
                      <button data-testid={`locker-staffpay-confirm-${p.id}`} disabled={confirming === p.id}
                        onClick={() => confirmPayment(p.id)}
                        style={{ background: GREEN, color: "#fff", border: "none", borderRadius: 99, padding: "5px 0", minWidth: 92, textAlign: "center", fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", boxSizing: "border-box", opacity: confirming === p.id ? 0.6 : 1 }}>
                        {confirming === p.id ? "…" : "Confirm"}
                      </button>
                    )}
                    <span style={{ fontSize: 12, fontWeight: 800, color: GREEN, whiteSpace: "nowrap", textAlign: "right", minWidth: 40 }}>{cash0(p.amount)}</span>
                  </div>
                ))}
                <div style={{ fontSize: 10.5, color: MUTED, marginTop: 8, lineHeight: 1.45 }}>
                  Each payment covers all your clients for that week — tap Confirm once you receive it.
                </div>
              </div>
            )}
          </div>
    </>
  ) : null;

  // ---------- board ----------
  return (
    <div style={S.page} data-testid="locker-board-page">
      <div style={S.wrap}>
        <div style={{ ...S.card, marginBottom: 14, padding: "16px 18px", display: "flex", alignItems: "flex-start", gap: 10 }} data-testid="locker-site-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: ".08em" }}>{kind === "staff" ? "Adherence staff" : "Locker site"}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: NAVY, marginTop: 2, lineHeight: 1.2 }} data-testid="locker-site-name">{kind === "staff" ? (staffHome?.name || "…") : siteName}</div>
            {kind === "staff" ? (
              siteName && <div style={{ fontSize: 13, color: TEXT, marginTop: 4 }} data-testid="locker-staff-site-name">{siteName}</div>
            ) : (
              (siteAddr.street || siteAddr.region) && (
                <div style={{ fontSize: 13, color: TEXT, marginTop: 4 }} data-testid="locker-site-address">
                  {siteAddr.street && <div>{siteAddr.street}</div>}
                  {siteAddr.region && <div>{siteAddr.region}</div>}
                </div>
              )
            )}
          </div>
          {/* Clear + Refresh live here (right of the site info) so the
              filter row below can hold the third dropdown */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0, marginTop: 2 }}>
            {SHOW_LOCKER_GRID && (
            <button
              data-testid="locker-filter-clear"
              onClick={() => { setFStatus(""); setFLoc(""); setFConsult(""); }}
              disabled={!filtering}
              style={{
                height: 34, padding: "0 14px", fontSize: 12, fontWeight: 700,
                color: filtering ? NAVY : MUTED, background: "#fff", borderRadius: 9,
                border: `1px solid ${filtering ? NAVY : BORDER}`,
                cursor: filtering ? "pointer" : "default", opacity: filtering ? 1 : 0.55,
                fontFamily: "inherit",
              }}
            >
              Clear
            </button>
            )}
            <button
              data-testid="locker-refresh"
              onClick={async () => { setRefreshing(true); await loadBoard(token); if (kind === "staff") await loadStaffHome(token); setRefreshing(false); }}
              disabled={refreshing}
              style={{
                height: 34, padding: "0 14px", fontSize: 12, fontWeight: 700,
                color: NAVY, background: "#fff", borderRadius: 9,
                border: `1px solid ${NAVY}`,
                cursor: refreshing ? "default" : "pointer", opacity: refreshing ? 0.55 : 1,
                fontFamily: "inherit",
              }}
            >
              {refreshing ? "…" : "Refresh"}
            </button>
            <button
              data-testid="locker-logout"
              onClick={logout}
              style={{
                height: 34, padding: "0 14px", fontSize: 12, fontWeight: 700,
                color: NAVY, background: "#fff", borderRadius: 9,
                border: `1px solid ${NAVY}`, cursor: "pointer", fontFamily: "inherit",
              }}
            >
              Log out
            </button>
          </div>
        </div>

        {/* staff: their balance summary sits right under their name; the
            Patients list comes first below, then fees and payments */}
        {staffTilesCard}

        {/* cash float — QUALIFIED (authorized across clients) / CASH IN / GIVEN /
            ON HAND, so the partner always knows their position. Rendered only
            once the board has loaded so nothing pops in late. Site-wide figures,
            so hidden from staff (they see their own payment tiles above). */}
        {board && cashBal && kind !== "staff" && (
          <div data-testid="locker-cash-balance" style={{ ...S.card, marginBottom: 14, padding: "10px 10px", display: "flex", gap: 6, flexWrap: "nowrap" }}>
            <div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>Qualified</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: NAVY, marginTop: 2, whiteSpace: "nowrap" }} data-testid="locker-bal-qualified">{fmtMoney0(cashBal.qualified)}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>Cash in</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: NAVY, marginTop: 2, whiteSpace: "nowrap" }} data-testid="locker-bal-in">{fmtMoney0(cashBal.topups_total)}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>Given</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: NAVY, marginTop: 2, whiteSpace: "nowrap" }} data-testid="locker-bal-given">{fmtMoney0(cashBal.given_total)}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>On hand</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: cashBal.remaining < 0 ? RED : GREEN, marginTop: 2, whiteSpace: "nowrap" }} data-testid="locker-bal-remaining">{fmtMoney0(cashBal.remaining)}</div>
            </div>
          </div>
        )}

        {/* patients — same collapsible per-client list the admin sees;
            tapping a client opens the exact same popup as their locker.
            Rendered only once the board has loaded so nothing pops in late. */}
        {board && (
        <div data-testid="locker-patients" style={{ ...S.card, marginBottom: 14, padding: "12px 18px" }}>
          <button
            type="button"
            data-testid="locker-patients-toggle"
            onClick={() => setPatOpen(!patOpen)}
            style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" }}
          >
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: MUTED, flex: 1, textAlign: "left" }}>
              Patients {patientRows.length > 0 && `(${patientRows.length})`}
            </span>
            {patOpen
              ? <ChevronDown style={{ width: 14, height: 14, color: MUTED, flexShrink: 0 }} />
              : <ChevronRight style={{ width: 14, height: 14, color: MUTED, flexShrink: 0 }} />}
          </button>
          {patOpen && (
            <div data-testid="locker-patients-list" style={{ marginTop: 4 }}>
              <div style={{ fontSize: 11, color: MUTED, paddingBottom: 4 }}>Tap a client to see each entry</div>
              {patientRows.length === 0 ? (
                <div style={{ fontSize: 12, color: MUTED, padding: "4px 0" }}>No clients yet.</div>
              ) : patientRows.map(({ cell, c }) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`locker-patient-${c.id}`}
                  onClick={() => openCell(cell)}
                  style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 2px", background: "none", border: "none", borderTop: `1px solid ${BORDER}`, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
                >
                  <span style={{ flex: 1, minWidth: 0, fontWeight: 700, color: TEXT }}>{c.first_name} {c.last_name}</span>
                  <span style={{ fontWeight: 800, color: NAVY, whiteSpace: "nowrap", textAlign: "right" }}>
                    {c.tablets_done} tabs · {fmtMoney0(c.cash_given)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* staff: fees + payments come right after their patient list */}
        {staffPayCards}

        {boardErr && <div style={S.err} data-testid="locker-board-error">{boardErr}</div>}

        {!board ? (
          <div style={{ textAlign: "center", color: MUTED, padding: 40, fontSize: 14 }}>Loading…</div>
        ) : SHOW_LOCKER_GRID && (
          <>
            <div data-testid="locker-filters" style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 3, paddingLeft: 11 }}>Disposition</div>
                <select
                  data-testid="locker-filter-disposition"
                  value={fStatus}
                  onChange={(e) => setFStatus(e.target.value)}
                  style={{ width: "100%", height: 40, padding: "0 10px", fontSize: 14, fontWeight: 600, color: TEXT, background: "#fff", border: `1px solid ${fStatus ? NAVY : BORDER}`, borderRadius: 10, boxSizing: "border-box" }}
                >
                  <option value="">All</option>
                  {statusOpts.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 3, paddingLeft: 11 }}>Storage</div>
                <select
                  data-testid="locker-filter-storage"
                  value={fLoc}
                  onChange={(e) => setFLoc(e.target.value)}
                  style={{ width: "100%", height: 40, padding: "0 10px", fontSize: 14, fontWeight: 600, color: TEXT, background: "#fff", border: `1px solid ${fLoc ? NAVY : BORDER}`, borderRadius: 10, boxSizing: "border-box" }}
                >
                  <option value="">All</option>
                  {locOpts.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 3, paddingLeft: 11 }}>Consultation</div>
                <select
                  data-testid="locker-filter-consultation"
                  value={fConsult}
                  onChange={(e) => setFConsult(e.target.value)}
                  style={{ width: "100%", height: 40, padding: "0 10px", fontSize: 14, fontWeight: 600, color: TEXT, background: "#fff", border: `1px solid ${fConsult ? NAVY : BORDER}`, borderRadius: 10, boxSizing: "border-box" }}
                >
                  <option value="">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            {filtering && (
              <div data-testid="locker-filter-count" style={{ fontSize: 12, color: MUTED, fontWeight: 600, marginBottom: 6, textAlign: "center" }}>
                Showing {matchCount} of {occupiedCount} clients
              </div>
            )}
          <div
            data-testid="locker-grid"
            style={{
              display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6,
              background: "#C4C9CE", border: "1px solid #A9AEB4", borderRadius: 10, padding: 8,
              boxShadow: "0 2px 8px rgba(0,0,0,.15)",
            }}
          >
            {board.filter((cell) => cell.n != null).map((cell) => {
              const c = cell.client;
              const occupied = cell.occupied && c;
              const complete = occupied && c.complete;
                  return (
                <button
                  key={cell.n}
                  data-testid={`locker-cell-${cell.n}`}
                  disabled={!occupied}
                  onClick={() => openCell(cell)}
                  style={{
                    position: "relative", aspectRatio: "3/4.3", borderRadius: 5,
                    border: "1px solid #AEB4BA",
                    background: "linear-gradient(180deg,#EDEFF1 0%,#D9DDE1 55%,#CBD0D4 100%)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,.8), 0 1px 2px rgba(0,0,0,.10)",
                    cursor: occupied ? "pointer" : "default", padding: 0, overflow: "hidden", fontFamily: "inherit",
                    opacity: filtering && !cellMatch(cell) ? 0.25 : 1,
                    transition: "opacity .15s ease",
                  }}
                >
                  {/* louvered vents (steel locker door) */}
                  <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", width: "52%", height: 14, background: "repeating-linear-gradient(180deg,#8F959B 0 2px,transparent 2px 5px)" }} />
                  {/* door number */}
                  <span style={{ position: "absolute", top: 26, left: "50%", transform: "translateX(-50%)", fontSize: 10, fontWeight: 800, color: "#5B6167" }}>{cell.n}</span>
                  {/* electronic keypad */}
                  <div style={{ position: "absolute", right: 6, top: "40%", width: 11, height: 22, background: "#2B2F33", borderRadius: 2, boxShadow: "inset 0 1px 2px rgba(0,0,0,.6)" }}>
                    <div style={{ width: 5, height: 3, margin: "3px auto 0", borderRadius: 1, background: occupied ? (c.hold ? "#F1948A" : complete ? "#7EE787" : "#FFD166") : "#555" }} />
                  </div>
                  {/* nameplate */}
                  {occupied ? (
                    <div style={{
                      position: "absolute", left: 5, right: 5, bottom: 6, borderRadius: 4,
                      background: c.hold ? "#FBEAE5" : complete ? "#E7F2EC" : "#fff",
                      border: `1px solid ${c.hold ? RED : complete ? GREEN : BORDER}`,
                      padding: "4px 3px", display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                    }}>
                      <span data-testid={`locker-cell-status-${cell.n}`}
                        style={{ fontSize: 8, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em", lineHeight: 1, color: (c.client_status || "Active") === "Active" ? GREEN : (c.client_status || "Active") === "Pending" ? "#B7791F" : MUTED }}>
                        {c.client_status || "Active"}
                      </span>
                      <span style={{ fontSize: 9, fontWeight: 800, lineHeight: 1.15, textAlign: "center", color: TEXT }}>
                        {c.first_name}<br />{c.last_name}
                      </span>
                      <span style={{ fontSize: 8, fontWeight: 700, color: c.hold ? RED : complete ? GREEN : MUTED }}>
                        {c.hold ? "CALL HEPCURE" : complete ? "COMPLETE" : `${c.tablets_done}/${c.total_tablets} tabs`}
                      </span>
                    </div>
                  ) : (
                    <div style={{ position: "absolute", left: 5, right: 5, bottom: 6, borderRadius: 4, background: "#F4F5F6", border: `1px dashed ${BORDER}`, padding: "5px 3px", fontSize: 9, fontWeight: 700, color: MUTED, textAlign: "center", letterSpacing: 1 }}>
                      EMPTY
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          </>
        )}

        {/* ---------- client popup — medication view and cash view share this
            exact same box, so opening Cash never changes the popup's size ---------- */}
        {selected && selected.client && (
          <div
            data-testid="locker-client-modal"
            onClick={() => (cashOpen ? setCashOpen(false) : setSelected(null))}
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 50, touchAction: "none" }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{ ...S.card, maxWidth: 360, width: "100%", padding: 0, overflow: "hidden", textAlign: "center" }}>
              <div style={{ padding: "12px 16px 0", overflowY: "auto", overscrollBehavior: "none", touchAction: "pan-y", maxHeight: "calc(100dvh - 64px)" }}>
              {selected.client.has_photo ? (
                <img
                  src={photoOf(selected.client)}
                  alt="Client ID — tap to view full screen"
                  data-testid="locker-modal-photo"
                  onClick={() => setPhotoFull(true)}
                  style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", border: `3px solid ${NAVY}`, background: "#D8D5CE", margin: "0 auto 8px", display: "block", cursor: "zoom-in" }}
                />
              ) : (
                <div
                  data-testid="locker-modal-no-photo"
                  style={{ width: 80, height: 80, borderRadius: "50%", background: "#D8D5CE", border: `3px solid ${NAVY}`, margin: "0 auto 8px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 800, color: MUTED }}
                >
                  {selected.client.first_name?.[0]}{selected.client.last_name?.[0]}
                </div>
              )}
              <div style={{ fontSize: 18, fontWeight: 800 }} data-testid="locker-modal-name">
                {selected.client.first_name} {selected.client.last_name}
              </div>
              <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }} data-testid="locker-modal-dob">
                DOB: {selected.client.dob}
              </div>
              {/* client facts — clean label/value list. Edit swaps the value
                  for an inline dropdown with Save/✕ pills, all in one row. */}
              <div style={{ margin: "12px 2px 0", border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", textAlign: "left" }} data-testid="locker-modal-facts">
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: MUTED, width: 92, flexShrink: 0 }}>Medication</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: TEXT, flex: 1 }} data-testid="locker-modal-med">{selected.client.medication || "—"}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderTop: `1px solid ${BORDER}` }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: MUTED, width: 92, flexShrink: 0 }}>Storage</span>
                  {locEdit ? (
                    <>
                      <select
                        data-testid="locker-storage-select"
                        value={locChoice}
                        onChange={(e) => setLocChoice(e.target.value)}
                        style={editSelect}
                      >
                        <option value="">— Not set —</option>
                        {locations.map((l) => <option key={l} value={l}>{l}</option>)}
                      </select>
                      <button
                        data-testid="locker-storage-save"
                        onClick={saveLocation}
                        disabled={locBusy}
                        style={{ width: 44, textAlign: "center", height: 24, padding: 0, background: GREEN, color: "#fff", border: "none", borderRadius: 999, fontSize: 10.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", opacity: locBusy ? 0.7 : 1, flexShrink: 0 }}
                      >
                        {locBusy ? "…" : "Save"}
                      </button>
                      <button
                        data-testid="locker-storage-cancel"
                        onClick={() => { setLocEdit(false); setLocErr(""); }}
                        style={{ height: 24, padding: "0 10px", background: "#fff", color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 999, fontSize: 10.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 13, fontWeight: 600, color: TEXT, flex: 1 }} data-testid="locker-modal-storage">{selected.client.location || "—"}</span>
                      <button
                        data-testid="locker-storage-edit"
                        onClick={() => { setLocChoice(selected.client.location || ""); setLocEdit(true); setLocErr(""); }}
                        style={{ height: 24, padding: "0 11px", background: "#fff", color: NAVY, border: `1px solid ${NAVY}`, borderRadius: 999, fontSize: 10.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}
                      >
                        Edit
                      </button>
                    </>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderTop: `1px solid ${BORDER}` }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: MUTED, width: 92, flexShrink: 0 }}>Consultation</span>
                  {conEdit ? (
                    <>
                      <select
                        data-testid="locker-consultation-select"
                        value={conChoice}
                        onChange={(e) => setConChoice(e.target.value)}
                        style={{ ...editSelect, maxWidth: 130 }}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                      <button
                        data-testid="locker-consultation-save"
                        onClick={saveConsultation}
                        disabled={conBusy}
                        style={{ width: 44, textAlign: "center", height: 24, padding: 0, background: GREEN, color: "#fff", border: "none", borderRadius: 999, fontSize: 10.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", opacity: conBusy ? 0.7 : 1, flexShrink: 0 }}
                      >
                        {conBusy ? "…" : "Save"}
                      </button>
                      <button
                        data-testid="locker-consultation-cancel"
                        onClick={() => { setConEdit(false); setConErr(""); }}
                        style={{ height: 24, padding: "0 10px", background: "#fff", color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 999, fontSize: 10.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 13, fontWeight: 600, color: (selected.client.consultation || "no") === "yes" ? GREEN : TEXT, flex: 1 }} data-testid="locker-modal-consultation">
                        {(selected.client.consultation || "no") === "yes" ? "Yes" : "No"}
                      </span>
                      <button
                        data-testid="locker-consultation-edit"
                        onClick={() => { setConChoice(selected.client.consultation || "no"); setConEdit(true); setConErr(""); }}
                        style={{ height: 24, padding: "0 11px", background: "#fff", color: NAVY, border: `1px solid ${NAVY}`, borderRadius: 999, fontSize: 10.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}
                      >
                        Edit
                      </button>
                    </>
                  )}
                </div>
              </div>
              {/* notes — collapsible. Yes/No tells at a glance whether any
                  notes exist; partner AND admin can update all three fields;
                  Save sits in the header beside Hide (only when changed).
                  Sits outside the med/cash views so both stay the same size. */}
              <div style={{ margin: "10px 2px 0", border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", textAlign: "left" }}>
                <div
                  role="button"
                  tabIndex={0}
                  data-testid="locker-notes-toggle"
                  onClick={() => { setNotesOpen((v) => !v); setNotesPhone(selected.client.phone || ""); setNotesAddr(selected.client.notes || ""); setNotesInfo(selected.client.notes_additional || ""); setNotesErr(""); }}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "8px 12px", background: "#FAF9F6", cursor: "pointer", boxSizing: "border-box" }}
                >
                  <span style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 }}>Notes</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {notesOpen ? (
                      <button
                        data-testid="locker-notes-save"
                        onClick={(e) => { e.stopPropagation(); saveNotes(); }}
                        disabled={notesBusy || !notesDirty}
                        style={{ height: 26, padding: "0 14px", background: GREEN, color: "#fff", border: "none", borderRadius: 999, fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", opacity: notesBusy || !notesDirty ? 0.55 : 1 }}
                      >
                        {notesBusy ? "…" : "Save"}
                      </button>
                    ) : (
                      <span data-testid="locker-notes-status" style={{ fontSize: 11, fontWeight: 800, color: hasNotes ? GREEN : "#9AA0A6" }}>{hasNotes ? "Yes" : "No"}</span>
                    )}
                    <span style={{ fontSize: 11, fontWeight: 800, color: NAVY }}>
                      {notesOpen ? "▾ Hide" : `▸ ${hasNotes ? "View" : "Add"}`}
                    </span>
                  </span>
                </div>
                {notesOpen && (
                  <div style={{ padding: "10px 12px 12px", borderTop: `1px solid ${BORDER}` }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: MUTED, marginBottom: 4, paddingLeft: 11 }}>Telephone</div>
                    <input
                      data-testid="locker-notes-phone"
                      value={notesPhone}
                      onChange={(e) => setNotesPhone(fmtPhone(e.target.value))}
                      placeholder="(555) 555-9999"
                      inputMode="tel"
                      style={{ ...S.input, width: "100%", height: 34, padding: "0 10px", fontSize: 13, textTransform: "none", letterSpacing: 0, boxSizing: "border-box", textAlign: "left", fontWeight: 600 }}
                    />
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: MUTED, marginBottom: 4, marginTop: 10, paddingLeft: 11 }}>Address / Contact</div>
                    <AutoArea
                      data-testid="locker-notes-address"
                      value={notesAddr}
                      onChange={(e) => setNotesAddr(e.target.value)}
                      placeholder="Address, shelter, contact person…"
                      style={{ ...S.input, width: "100%", padding: "8px 10px", fontSize: 12, textTransform: "none", letterSpacing: 0, boxSizing: "border-box", textAlign: "left", fontWeight: 600 }}
                    />
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: MUTED, marginBottom: 4, marginTop: 10, paddingLeft: 11 }}>Additional information</div>
                    <AutoArea
                      data-testid="locker-notes-additional"
                      value={notesInfo}
                      onChange={(e) => setNotesInfo(e.target.value)}
                      placeholder="Dated instructions, preferences…"
                      style={{ ...S.input, width: "100%", padding: "8px 10px", fontSize: 12, textTransform: "none", letterSpacing: 0, boxSizing: "border-box", textAlign: "left", fontWeight: 600 }}
                    />
                    {notesErr && <div data-testid="locker-notes-error" style={{ fontSize: 11, color: RED, fontWeight: 600, marginTop: 4 }}>{notesErr}</div>}
                  </div>
                )}
              </div>
              {!cashOpen ? (
                <>
              <div style={{ fontSize: 12, fontWeight: 700, color: NAVY, marginTop: 8 }} data-testid="locker-modal-week">
                {selected.n ? `Locker ${selected.n} · ` : ""}{selected.client.tablets_done} of {selected.client.total_tablets} tablets given
              </div>
              {/* reserved second line — the cash view's "remaining" line sits
                  here, so both views are the exact same height */}
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 4, visibility: "hidden" }} aria-hidden="true">&nbsp;</div>

              {/* recorded entries — always occupies the same 4-row space */}
              <PagedEntries entries={selected.client.entries || []} page={medPage} setPage={setMedPage} kind="tabs" onRemove={removeEntry} />

              {/* action area — fixed height: form, hold notice and complete note
                  all occupy the same space, so nothing ever resizes the popup */}
              <div style={{ marginTop: 10, minHeight: 120, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              {selected.client.hold ? (
                <div data-testid="locker-hold-notice"
                  style={{ background: "#FBEAE5", border: `2px solid ${RED}`, color: RED, borderRadius: 10, padding: "12px", fontSize: 14, fontWeight: 700, lineHeight: 1.45 }}>
                  Do not give medication to this client.<br />
                  The client must call HepCURE at <span style={{ whiteSpace: "nowrap" }}>1-844-925-CURE (2873)</span> before their next pickup.
                </div>
              ) : selected.client.complete ? (
                <div style={{ fontSize: 13, color: MUTED, fontWeight: 600 }} data-testid="locker-complete-note">
                  Treatment complete<br />HepCURE will reassign this locker.
                </div>
              ) : (
                <div>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "nowrap" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4, textAlign: "center" }}>Tabs</div>
                      <input data-testid="locker-dispense-tablets" type="number" min="1" inputMode="numeric" value={tabsAmt}
                        onChange={(e) => setTabsAmt(e.target.value)}
                        style={{ ...S.input, letterSpacing: 0, textTransform: "none", height: 44 }} />
                    </div>
                    <div style={{ flex: 1.4, minWidth: 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4, textAlign: "left", paddingLeft: 15 }}>Date</div>
                      <input data-testid="locker-dispense-date" type="date" value={svcDate} max={new Date().toLocaleDateString("en-CA")}
                        onChange={(e) => setSvcDate(e.target.value)}
                        style={{ ...S.input, letterSpacing: 0, textTransform: "none", height: 44, fontSize: 14 }} />
                    </div>
                    <button
                      data-testid="locker-cash-open"
                      onClick={() => { setCashOpen(true); setCashErr(""); setCashMsg(""); setCashAmt(""); setCashDate(new Date().toLocaleDateString("en-CA")); setCashPage(0); }}
                      style={{ height: 44, padding: "0 14px", flexShrink: 0, background: "#fff", color: NAVY, border: `2px solid ${NAVY}`, borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}
                    >
                      Cash
                    </button>
                  </div>
                  <button
                    data-testid="locker-dispense-button"
                    style={{ ...S.btnGreen, marginTop: 10, opacity: dispensing ? 0.7 : 1 }}
                    onClick={dispense}
                    disabled={dispensing}
                  >
                    {dispensing ? "Submitting…" : "Record dispense"}
                  </button>
                </div>
              )}
              </div>
                </>
              ) : (
                <>
              {/* ---------- cash view (same box as the medication view) ---------- */}
              <div style={{ fontSize: 12, fontWeight: 700, color: NAVY, marginTop: 8 }} data-testid="locker-cash-progress">
                {selected.n ? `Locker ${selected.n} · ` : ""}{fmtMoney(selected.client.cash_given)} of {fmtMoney(selected.client.total_cash)} cash given
              </div>
              {/* always reserves its line (hidden when complete/unset) so the
                  box height matches the medication view exactly */}
              <div
                style={{ fontSize: 13, fontWeight: 700, color: GREEN, marginTop: 4, visibility: (selected.client.total_cash > 0 && !selected.client.cash_complete) ? "visible" : "hidden" }}
                data-testid="locker-cash-remaining"
              >
                {(selected.client.total_cash > 0 && !selected.client.cash_complete)
                  ? `${fmtMoney(Math.max(0, Math.round((selected.client.total_cash - selected.client.cash_given) * 100) / 100))} remaining of the qualified amount`
                  : " "}
              </div>

              {/* recorded cash entries — always occupies the same 4-row space */}
              <PagedEntries entries={selected.client.cash_entries || []} page={cashPage} setPage={setCashPage} kind="cash" onRemove={removeEntry} />

              {/* action area — fixed height, same space as the medication view */}
              <div style={{ marginTop: 10, minHeight: 120, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              {selected.client.hold ? (
                <div data-testid="locker-cash-hold-notice"
                  style={{ background: "#FBEAE5", border: `2px solid ${RED}`, color: RED, borderRadius: 10, padding: "12px", fontSize: 14, fontWeight: 700, lineHeight: 1.45 }}>
                  Do not give cash or medication to this client.<br />
                  The client must call HepCURE at <span style={{ whiteSpace: "nowrap" }}>1-844-925-CURE (2873)</span> before their next pickup.
                </div>
              ) : (selected.client.consultation || "no") !== "yes" ? (
                <div data-testid="locker-cash-consult-warning"
                  style={{ background: "#FBEAE5", border: `2px solid ${RED}`, color: RED, borderRadius: 10, padding: "12px", fontSize: 14, fontWeight: 700, lineHeight: 1.45 }}>
                  Consultation not completed.<br />
                  Complete before giving cash to client.
                </div>
              ) : !(selected.client.total_cash > 0) ? (
                <div style={{ fontSize: 13, color: MUTED, fontWeight: 600 }} data-testid="locker-cash-none">
                  No qualified cash amount has been set for this client yet.<br />HepCURE can add it.
                </div>
              ) : selected.client.cash_complete ? (
                <div style={{ fontSize: 13, color: MUTED, fontWeight: 600 }} data-testid="locker-cash-complete-note">
                  Qualified cash amount fully given.
                </div>
              ) : (
                <div>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "nowrap" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4, textAlign: "center" }}>Cash $</div>
                      <input data-testid="locker-cash-amount" type="number" min="0" step="0.01" inputMode="decimal" value={cashAmt}
                        onChange={(e) => setCashAmt(e.target.value)}
                        style={{ ...S.input, letterSpacing: 0, textTransform: "none", height: 44 }} />
                    </div>
                    <div style={{ flex: 1.4, minWidth: 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4, textAlign: "left", paddingLeft: 15 }}>Date</div>
                      <input data-testid="locker-cash-date" type="date" value={cashDate} max={new Date().toLocaleDateString("en-CA")}
                        onChange={(e) => setCashDate(e.target.value)}
                        style={{ ...S.input, letterSpacing: 0, textTransform: "none", height: 44, fontSize: 14 }} />
                    </div>
                  </div>
                  <button
                    data-testid="locker-cash-record"
                    style={{ ...S.btnGreen, marginTop: 10, opacity: cashBusy ? 0.7 : 1 }}
                    onClick={recordCash}
                    disabled={cashBusy}
                  >
                    {cashBusy ? "Submitting…" : "Record payment"}
                  </button>
                </div>
              )}
              </div>
                </>
              )}

              {/* message slot — fixed two-line height; success or error appears
                  here without ever changing the popup size */}
              <div style={{ marginTop: 8, height: 38, overflow: "hidden" }} data-testid="locker-modal-message">
                {cashOpen ? (
                  (cashErr || locErr || conErr)
                    ? <div style={{ ...S.err, marginTop: 0 }} data-testid="locker-cash-error">{cashErr || locErr || conErr}</div>
                    : cashMsg
                      ? <div data-testid="locker-cash-success"
                          style={{ background: "#E7F2EC", border: `1px solid ${GREEN}`, color: GREEN, borderRadius: 10, padding: "8px 12px", fontSize: 13, fontWeight: 700 }}>
                          {cashMsg}
                        </div>
                      : null
                ) : (
                  (dispenseErr || locErr || conErr)
                    ? <div style={{ ...S.err, marginTop: 0 }} data-testid="locker-dispense-error">{dispenseErr || locErr || conErr}</div>
                    : dispensedMsg
                      ? <div data-testid="locker-dispense-success"
                          style={{ background: "#E7F2EC", border: `1px solid ${GREEN}`, color: GREEN, borderRadius: 10, padding: "8px 12px", fontSize: 13, fontWeight: 700 }}>
                          {dispensedMsg}
                        </div>
                      : null
                )}
              </div>

              </div>

              {/* pinned footer — Close is ALWAYS visible, never scrolled away */}
              <div style={{ padding: "2px 16px 8px", borderTop: `1px solid ${BORDER}`, flexShrink: 0 }}>
              <button
                data-testid="locker-modal-close"
                onClick={() => (cashOpen ? setCashOpen(false) : setSelected(null))}
                style={{ marginTop: 4, background: "none", border: "none", color: MUTED, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                Close
              </button>
              </div>
            </div>
          </div>
        )}

        {/* ---------- full-screen photo viewer ---------- */}
        {photoFull && selected?.client && (
          <div
            data-testid="locker-photo-fullscreen"
            onClick={() => setPhotoFull(false)}
            style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100dvh", minHeight: "100vh", background: "rgba(0,0,0,.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, boxSizing: "border-box", zIndex: 60 }}
          >
            <button
              data-testid="locker-photo-fullscreen-close"
              onClick={() => setPhotoFull(false)}
              aria-label="Close photo"
              style={{ position: "absolute", top: 14, right: 14, width: 42, height: 42, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.16)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <X style={{ width: 20, height: 20 }} strokeWidth={2.5} />
            </button>
            <img
              src={photoOf(selected.client)}
              alt="Client ID — full view"
              data-testid="locker-photo-fullscreen-img"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8 }}
            />
          </div>
        )}
      </div>
    </div>
  );
}